import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AfslTemplate, EmailTemplate, PracticeTemplate, SavedParameterWithAfsl, SchedulerState } from './types';
import { fetchFilteredReportClients } from 'src/features/reporting/store/thunks';
import { FilteredReportClientViewModel, ScheduledEvent } from 'src/features/reporting/store/types';
import { fetchAfslTemplates, fetchPracticeTemplates, fetchScheduledEventById } from './thunks';
import { fetchReportParametersList } from 'src/features/clients/client/reportsV2/store/thunks';
import { SavedParameter } from 'src/features/clients/client/reportsV2/store/types';
import { fetchAccountSearchResults } from 'src/features/bulk/email/search/store/thunks';
import { AccountSearchResult } from 'src/features/bulk/email/search/store/types';

const initialState: SchedulerState = {
  afsls: [],
  clients: [],
  practices: [],
  advisers: [],
  groups: [],
  savedParametersList: [],
  savedParameter: null,
  editingEvent: undefined,
  accountSearchResult: [],
  selectedTemplate: undefined,
  allowStaticClients: false,
  afslTemplates: [],
  practiceTemplates: [],
};

export const schedulerSlice = createSlice({
  name: '@@scheduler',
  initialState,
  reducers: {
    resetEditingSchedulerEvent: (state) => {
      state.editingEvent = undefined;
      state.selectedTemplate = undefined;
    },
    setSavedParameter: (state, action: PayloadAction<SavedParameterWithAfsl | null>) => {
      state.savedParameter = action.payload;
    },
    setAllowStaticClients: (state, action: PayloadAction<boolean>) => {
      state.allowStaticClients = action.payload;
    },
    setSelectedTemplate: (state, action: PayloadAction<EmailTemplate | undefined>) => {
      state.selectedTemplate = action.payload;
    },
    resetEmailTemplates: (state) => {
      state.afslTemplates = [];
      state.practiceTemplates = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilteredReportClients.fulfilled, (state, action: PayloadAction<FilteredReportClientViewModel>) => {
      state.clients = action.payload.clients;
      state.afsls = action.payload.afsls;
      state.advisers = action.payload.advisers;
      state.groups = action.payload.groups;
      state.practices = action.payload.practices;
    });
    builder.addCase(fetchScheduledEventById.pending, (state) => {
      state.editingEvent = null;
    });
    builder.addCase(fetchScheduledEventById.fulfilled, (state, action: PayloadAction<ScheduledEvent>) => {
      state.editingEvent = action.payload;
    });
    builder.addCase(fetchAfslTemplates.pending, (state) => {
      state.afslTemplates = [];
    });
    builder.addCase(fetchAfslTemplates.fulfilled, (state, action: PayloadAction<AfslTemplate[]>) => {
      state.afslTemplates = action.payload;
    });
    builder.addCase(fetchPracticeTemplates.pending, (state) => {
      state.practiceTemplates = [];
    });
    builder.addCase(fetchPracticeTemplates.fulfilled, (state, action: PayloadAction<PracticeTemplate[]>) => {
      state.practiceTemplates = action.payload;
    });
    builder.addCase(fetchReportParametersList.fulfilled, (state, action: PayloadAction<SavedParameter[]>) => {
      state.savedParametersList = action.payload.filter(
        (param: SavedParameter, index: number, self: SavedParameter[]) => index === self.findIndex((p) => p.id === param.id)
      );
    });
    builder.addCase(fetchAccountSearchResults.fulfilled, (state, action: PayloadAction<AccountSearchResult[]>) => {
      state.accountSearchResult = action.payload;
    });
  },
});
